import { useCallback, useEffect, useRef, useState } from 'react';
import { MOVABLE_DOCUMENT_CATEGORIES, type MovableDocumentCategory } from '@hector/shared-types';
import {
  describeDestinationIssue,
  withCase,
  withCategory,
  type DestinationValue,
} from './destinationModel';

export interface UseDestinationSelectionOptions {
  /** Dossier présélectionné — le dossier courant, ou celui de la conversation. */
  initialCaseId?: string;
  /**
   * Onglet présélectionné.
   *
   * WHY il est posé D'EMBLÉE, même sans dossier : l'avocat qui enregistre depuis
   * une jurisprudence doit voir « Mes recherches juridiques » déjà choisi, et le
   * retrouver tel quel au moment où il désigne enfin un dossier. Faire dépendre
   * l'onglet du dossier obligerait à le re-choisir après coup.
   */
  defaultCategory?: MovableDocumentCategory;
  /**
   * Change de valeur pour réinitialiser la sélection — les appelants y passent
   * leur `open`.
   *
   * WHY : ces fenêtres restent MONTÉES en permanence (le panneau de décision, par
   * exemple, vit dans la coque globale). Sans réinitialisation à l'ouverture, un
   * second enregistrement rouvrirait sur les choix du précédent — et rangerait la
   * pièce dans le dossier d'un autre client sans que rien ne l'annonce.
   */
  resetToken?: unknown;
}

export interface DestinationSelection {
  value: DestinationValue;
  setCaseId: (caseId: string) => void;
  setCategory: (category: MovableDocumentCategory) => void;
  setFolderPath: (folderPath: string[]) => void;
  /** Raison pour laquelle la destination n'est pas encore écrivable, ou `null`. */
  issue: string | null;
}

const FALLBACK_CATEGORY: MovableDocumentCategory = MOVABLE_DOCUMENT_CATEGORIES[0];

/**
 * État d'une sélection de destination, avec ses règles de présélection.
 *
 * Toute la logique de transition (remise à la racine quand le dossier ou l'onglet
 * change, validité) vit dans `destinationModel.ts`, en fonctions pures testées.
 * Ce hook n'ajoute que le cycle de vie React.
 */
export function useDestinationSelection({
  initialCaseId,
  defaultCategory,
  resetToken,
}: UseDestinationSelectionOptions = {}): DestinationSelection {
  const initial = useCallback(
    (): DestinationValue => ({
      caseId: initialCaseId ?? '',
      category: defaultCategory ?? FALLBACK_CATEGORY,
      folderPath: [],
    }),
    [initialCaseId, defaultCategory],
  );

  const [value, setValue] = useState<DestinationValue>(initial);

  // Réinitialisation à chaque ouverture. `useRef` plutôt qu'une dépendance sur
  // `initial` : on ne veut PAS réinitialiser quand le dossier présélectionné se
  // charge en arrière-plan, seulement quand la fenêtre s'ouvre — sinon un choix
  // fait dans la seconde qui suit l'ouverture serait écrasé.
  const lastToken = useRef(resetToken);
  useEffect(() => {
    if (lastToken.current === resetToken) return;
    lastToken.current = resetToken;
    setValue(initial());
  }, [resetToken, initial]);

  // Le dossier présélectionné peut arriver APRÈS le premier rendu (chargement de
  // la conversation, du dossier courant). On l'adopte tant que l'avocat n'a rien
  // choisi lui-même — sans jamais écraser un choix explicite.
  useEffect(() => {
    if (!initialCaseId) return;
    setValue((current) => (current.caseId ? current : withCase(current, initialCaseId)));
  }, [initialCaseId]);

  const setCaseId = useCallback((caseId: string) => {
    setValue((current) => withCase(current, caseId));
  }, []);

  const setCategory = useCallback((category: MovableDocumentCategory) => {
    setValue((current) => withCategory(current, category));
  }, []);

  const setFolderPath = useCallback((folderPath: string[]) => {
    setValue((current) => ({ ...current, folderPath }));
  }, []);

  return { value, setCaseId, setCategory, setFolderPath, issue: describeDestinationIssue(value) };
}
